import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/catalyst/Button";
import {
	Dialog,
	DialogActions,
	DialogBody,
	DialogDescription,
	DialogTitle,
} from "@/components/catalyst/Dialog";
import { StripePaymentForm } from "@/components/stripe-payment-form";
import { orpc } from "@/orpc/client";

interface ReplacePaymentMethodDialogProps {
	isOpen: boolean;
	onClose: () => void;
	entityId: string | null;
}

export function ReplacePaymentMethodDialog({
	isOpen,
	onClose,
	entityId,
}: ReplacePaymentMethodDialogProps) {
	const queryClient = useQueryClient();
	const [clientSecret, setClientSecret] = useState<string | null>(null);

	const { mutate: replacePaymentMethod, isPending: isCreating } = useMutation(
		orpc.billingEntities.replacePaymentMethod.mutationOptions({
			onSuccess: (data) => {
				setClientSecret(data.clientSecret);
			},
			onError: (error) => {
				toast.error(`Failed to start replacement: ${error.message}`);
			},
		}),
	);

	const { mutateAsync: confirmPaymentMethodAsync } = useMutation(
		orpc.billingEntities.confirmPaymentMethod.mutationOptions(),
	);

	// Start the replacement process when dialog opens
	if (isOpen && !clientSecret && entityId && !isCreating) {
		replacePaymentMethod({ id: entityId });
	}

	const handleClose = () => {
		setClientSecret(null);
		onClose();
	};

	const handleSuccess = () => {
		queryClient.invalidateQueries({
			queryKey: orpc.billingEntities.list.key(),
		});
		toast.success("Card replaced successfully!");
		handleClose();
	};

	return (
		<Dialog open={isOpen} onClose={handleClose} size="lg">
			<DialogTitle>Replace Card</DialogTitle>
			<DialogDescription>
				Enter new card details to replace the existing payment method.
			</DialogDescription>

			{!clientSecret ? (
				<DialogBody>
					<div className="flex items-center justify-center py-8">
						<div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
					</div>
				</DialogBody>
			) : (
				entityId && (
					<StripePaymentForm
						clientSecret={clientSecret}
						onSubmit={async ({ stripe, elements }) => {
							const { error: submitError } = await elements.submit();
							if (submitError)
								return submitError.message ?? "An error occurred";

							const { error: confirmError, setupIntent } =
								await stripe.confirmSetup({
									elements,
									confirmParams: {
										return_url: window.location.href,
									},
									redirect: "if_required",
								});

							if (confirmError)
								return confirmError.message ?? "Failed to confirm setup";

							if (setupIntent?.status !== "succeeded") {
								return "Setup was not completed. Please try again.";
							}

							try {
								await confirmPaymentMethodAsync({ id: entityId });
								handleSuccess();
							} catch (err) {
								return err instanceof Error
									? err.message
									: "Failed to confirm payment method";
							}

							return undefined;
						}}
					>
						{({ paymentElement, error, isProcessing, isReady }) => (
							<>
								<DialogBody>
									<div className="space-y-4">
										{paymentElement}
										{error}
									</div>
								</DialogBody>
								<DialogActions>
									<Button
										plain
										onClick={handleClose}
										type="button"
										disabled={isProcessing}
									>
										Cancel
									</Button>
									<Button type="submit" disabled={!isReady || isProcessing}>
										{isProcessing ? "Processing..." : "Save Card"}
									</Button>
								</DialogActions>
							</>
						)}
					</StripePaymentForm>
				)
			)}
		</Dialog>
	);
}
